import { defineBlock } from "eddev/blocks"
import { Container, Grid, Col } from "@components/layout/Grid"
import { cn } from "@utils/tw"
import { Link } from "eddev/routing"
import { useIsMobile } from "@hooks/useViewportDerived"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Chip } from "@components/atoms/Chip"
import { VSpace } from "@components/layout/Spacer"
import { Heading } from "@components/atoms/Heading"
import { Typography } from "@components/atoms/Typography"
import { Button } from "@components/atoms/Button"
import { ResponsiveImageFragment } from "../../types.graphql"
import { useFocusAreas } from "@hooks/queries"
import { useHydrating } from "eddev/routing"

export const meta: BlockMeta = {
  title: "Focus Area Cards",
  category: "common",
  icon: "format-image",
  keywords: ["card"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

type FocusCardT = {
  title?: string
  content?: string
  image?: ResponsiveImageFragment
  latestProject?: string | null
  uri?: string
  imagePosition?: boolean
}

export default defineBlock("general/card-focus-area", (props) => {
  const focusAreas = useFocusAreas()
  const hydrating = useHydrating()

  if (!hydrating && focusAreas.data?.rsrpFocusAreas?.nodes) {
    return (
      <>
        {focusAreas.data.rsrpFocusAreas.nodes.map((area, i) => (
          <Container className="py-6" key={i}>
            <Grid>
              <Col initial={12}>
                {area.rsrpProjects?.nodes && (
                  <FocusAreaCard
                    title={area.name ?? ""}
                    content={area.description ?? undefined}
                    image={area.focusAreaFields?.cardImage ?? undefined}
                    latestProject={area.rsrpProjects.nodes.length > 0 ? area.rsrpProjects.nodes[0].uri : undefined}
                    uri={`/research-data/road-safety-research-program/${area.slug}`}
                    imagePosition={true}
                  />
                )}
              </Col>
            </Grid>
          </Container>
        ))}
      </>
    )
  }

  return <></>
})

const FocusAreaCard = (props: FocusCardT) => {
  const isMobile = useIsMobile()

  return (
    <div className="w-full flex rounded-md relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 p-0 border border-grey flex-col gap-0 box-border [&_.chip]:absolute [&_.chip]:top-6 [&_.chip]:left-6 [&_.button]:justify-between [&_a]:no-underline md:p-6 md:gap-7 md:flex-row md:[&_.chip]:relative md:[&_.chip]:top-auto md:[&_.chip]:left-auto aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
      {props.uri && !env.admin && <Link className="absolute inset-0" href={props.uri} />}
      <div
        className={cn(
          "aaa-animate relative pointer-events-none [&_div]:block [&_div]:w-full [&_div]:absolute [&_div]:inset-0 [&>div]:!h-full [&>div_img]:block w-full pt-[calc(100%*(288/408))] rounded-[6px] overflow-hidden md:w-[calc((100%-var(--spacing-7))*(5/12))] md:pt-[calc(((100%-var(--spacing-7))*(5/12))*(288/408))]",
          props.imagePosition && !isMobile ? "order-2" : "order-1",
        )}
      >
        <ResponsiveImage {...props.image} aspect={408 / 288} />
      </div>
      <div
        className={cn(
          "flex flex-col pointer-events-none [&_h1]:type-title-xl [&_h3]:type-title-m [&_h3]:my-0 [&_strong]:!font-[inherit] w-full items-start justify-between p-6 md:w-[calc((100%-var(--spacing-7))*(7/12))] md:pt-2 md:pr-6 md:pb-2 md:pl-0",
          props.imagePosition && !isMobile ? "order-1" : "order-2",
        )}
      >
        <div>
          <Chip color="grey">Focus Area</Chip>
          {!isMobile && <VSpace initial={5} />}
          <Heading as="h3">{props.title}</Heading>
          <VSpace initial={5} />
          <Typography as="p" dangerouslySetInnerHTML={{ __html: props.content?.replaceAll("\n", "</br />") ?? "" }} />
        </div>

        {(props.latestProject || props.uri) && (
          <div className="flex flex-wrap gap-4 relative z-[5] pointer-events-auto mt-7 w-full flex-col md:flex-row">
            {/* {props.latestProject && (
              <Button color="yellow" size="medium" href={props.latestProject}>
                Latest project
              </Button>
            )} */}

            {props.uri && (
              <Button color="yellow" size="medium" href={props.uri}>
                Find out more
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
